import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { scroller } from 'react-scroll'

import { FAQ_ROUTE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/faqConstants'
import {
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  MIXPANEL_CARE_NAVIGATOR_BUTTON_TEXT,
  MIXPANEL_CARE_NAVIGATOR_OPTIONS,
  S4C_EVENTS,
  S4C_HEADER_EVENTS,
  S4C_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { getCountryFlag } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/mixpanelUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { useCareNavigatorModal as useCareNavigatorModalShared } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useCareNavigatorModal'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'

import { useDefaultMixpanelPage } from './useDefaultMixpanelPage'
import { getDetectedCountryISO, getShouldShowLiveChat } from '../../data/appGlobals/appGlobalsSelectors'
import { getCurrentCountryName } from '../../data/currentCountry/currentCountrySelectors'
import {
  getCustomerInfo,
  getCustomerLaunchDate,
  getCustomerName,
  getCustomerPhone,
  getDefaultDirectCareNavigatorBookingLink,
  getIsCustomerLyra,
} from '../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../data/mixpanel'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getDirectDedicatedCareNavigatorInfo, getDirectDedicatedCareNavigatorLink } from '../../data/user/userActions'
import {
  getId,
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserInternational,
  getIsUserLoggedIn,
  getUserCountryCode,
  getUserDedicatedCareNavigatorInfo,
  getUserDirectDedicatedCareNavigatorLink,
  getUserLegalFirstName,
  getUserLegalLastName,
  getUsername,
  getUserSalesforceId,
} from '../../data/user/userSelectors'
import ChatLive from '../components/chatLive/ChatLive'
import { goToCareNavigatorSchedulingPage } from '../utils/onboardUtils'

type useCareNavigatorModalParams = {
  /** Mixpanel 'entryPoint' for click events on the CN modal */
  clickEntryPoint?: string
  /** Mixpanel 'CN_entrypoint' for the view general contact CN team page event */
  viewEventCNEntryPoint?: string
  /** If entryPoint isn't predetermined when the hook is called, use this function which will override `clickEntryPoint`. eg ForkedTriage entry point changes if the meds modal opens and the entry point is unknown until the CN modal is triggered. clickEntryPoint would not update in time. */
  getClickEntryPoint?: () => string
  showMinimalCTAText?: boolean
  /** Returns the mixpanel event name for the CN entry point click event */
  getCareNavigatorEntryPointClickEvent?: () => string
  showScheduleCall?: boolean
  isHomebaseFlow?: boolean
  disableBottomSheet?: boolean
  loggedOutFaqRoute?: string
}

export const useCareNavigatorModal = ({
  clickEntryPoint,
  viewEventCNEntryPoint,
  showMinimalCTAText,
  getClickEntryPoint,
  getCareNavigatorEntryPointClickEvent,
  showScheduleCall,
  isHomebaseFlow,
  disableBottomSheet,
  loggedOutFaqRoute,
}: useCareNavigatorModalParams) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isDedicatedCareNavigatorEnabled } = useFlags()
  const { activeLanguage } = useLyraIntl()
  const mixpanelPage = useDefaultMixpanelPage()
  const userId = useSelector(getId)
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const isUserInternational = useSelector(getIsUserInternational)
  const isUserAssumedInternational = useSelector(getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer)
  const isInternational = isLoggedIn ? isUserInternational : isUserAssumedInternational
  const isLyrian = useSelector(getIsCustomerLyra)
  const customerPhoneNumber = useSelector(getCustomerPhone)
  const userDedicatedCareNavigatorInfo = useSelector(getUserDedicatedCareNavigatorInfo)?.toJS()
  const showLiveChat = useSelector(getShouldShowLiveChat)
  const countryIsoCode = useSelector(getUserCountryCode)
  const detectedCountryIsoCode = useSelector(getDetectedCountryISO)
  const customerName = useSelector(getCustomerName)
  const countryName = useSelector(getCurrentCountryName)
  const userDirectDedicatedCareNavigatorLink = useSelector(getUserDirectDedicatedCareNavigatorLink)
  const defaultDirectCareNavigatorBookingLink = useSelector(getDefaultDirectCareNavigatorBookingLink)
  const firstName = useSelector(getUserLegalFirstName)
  const lastName = useSelector(getUserLegalLastName)
  const email = useSelector(getUsername)
  const salesforceId = useSelector(getUserSalesforceId)
  const launchDate = useSelector(getCustomerLaunchDate)
  const customerDisplayName = useSelector(getCustomerInfo)
  const isMicrositeHomepage = ['/'].includes(pathname)

  useFetcher([
    [
      async () => await dispatch(getDirectDedicatedCareNavigatorInfo),
      [userId],
      isDedicatedCareNavigatorEnabled && userDedicatedCareNavigatorInfo == null,
    ],
  ])

  const getEntryPoint = useCallback(() => {
    return getClickEntryPoint ? getClickEntryPoint() : clickEntryPoint
  }, [clickEntryPoint, getClickEntryPoint])

  const getOnPressMixpanelProps = useCallback(() => {
    return {
      [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.GENERAL_CN_CONTACT_PAGE,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: getEntryPoint(),
      ...getCountryFlag(isUserInternational),
      [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.CLICK,
    }
  }, [getEntryPoint, isUserInternational])

  const onEntryPointPressedTrack = useCallback(() => {
    const entrypoint = getEntryPoint()
    const commonProps = {
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: entrypoint,
      ...mixpanelPage,
      [S4C_PROPERTIES.CONTACT_OPTIONS]: MIXPANEL_CARE_NAVIGATOR_OPTIONS({
        isInternationalUser: isInternational,
        isSidePanel: true,
      }),
      [COMMON_MIXPANEL_PROPERTIES.BUTTON_TEXT]: MIXPANEL_CARE_NAVIGATOR_BUTTON_TEXT(isUserInternational),
      ...getCountryFlag(isUserInternational),
    }
    dispatch(
      trackEventWithObj({
        event:
          (getCareNavigatorEntryPointClickEvent && getCareNavigatorEntryPointClickEvent()) ||
          S4C_EVENTS.CLICK_TO_CONTACT_CN,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.CLICK,

        ...commonProps,
      }),
    )
    dispatch(
      trackEventWithObj({
        event: S4C_EVENTS.VIEW_GENERAL_CONTACT_CN_TEAM,
        [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.PAGE_LOAD,
        ...commonProps,
        ...(viewEventCNEntryPoint !== undefined && {
          [S4C_PROPERTIES.CN_entrypoint]: viewEventCNEntryPoint,
        }),
      }),
    )
  }, [
    dispatch,
    getCareNavigatorEntryPointClickEvent,
    getEntryPoint,
    isInternational,
    isUserInternational,
    mixpanelPage,
    viewEventCNEntryPoint,
  ])

  const onPressEmailTrack = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: COMMON_MIXPANEL_EVENTS.CLICK_EMAIL_CN,
        ...getOnPressMixpanelProps(),
      }),
    )
  }, [dispatch, getOnPressMixpanelProps])

  const onPressFAQTrack = useCallback(() => {}, [])

  const onPressPhoneCallTrack = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: COMMON_MIXPANEL_EVENTS.CLICK_PHONE_CN,
        ...getOnPressMixpanelProps(),
      }),
    )
  }, [dispatch, getOnPressMixpanelProps])

  const onPressScheduleCallTrack = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: COMMON_MIXPANEL_EVENTS.CLICK_SCHEDULE_A_CN_CALL,
        ...getOnPressMixpanelProps(),
      }),
    )
  }, [dispatch, getOnPressMixpanelProps])

  const onPressLiveChatTrack = useCallback(() => {
    dispatch(
      trackEventWithObj({
        event: S4C_EVENTS.CLICK_CHAT_LIVE,
        ...getOnPressMixpanelProps(),
      }),
    )
  }, [dispatch, getOnPressMixpanelProps])

  const onPressPhoneCallInternational = useCallback(() => {
    return navigate('/care-team-phone-numbers')
  }, [navigate])

  const onPressFAQ = useCallback(() => {
    if (isLoggedIn) {
      openUrl(`${window.location.origin}${FAQ_ROUTE}`)
    } else if (loggedOutFaqRoute) {
      navigate(loggedOutFaqRoute)
    } else {
      if (isMicrositeHomepage) {
        scroller.scrollTo('faq', { smooth: true })
      } else {
        navigate('/#faq')
      }
    }
  }, [isLoggedIn, isMicrositeHomepage, loggedOutFaqRoute, navigate])

  const onCareNavigatorIconHoverTrack = useCallback(
    ({ hoverText }: { hoverText: string }) => {
      dispatch(
        trackEventWithObj({
          event: S4C_HEADER_EVENTS.HOVER_FOR_ICON_EXPLANATION,
          iconText: hoverText,
          ...mixpanelPage,
        }),
      )
    },
    [dispatch, mixpanelPage],
  )

  const loadDedicatedCareNavigator = useCallback(() => {
    dispatch(getDirectDedicatedCareNavigatorInfo(userId))
  }, [dispatch, userId])

  const onPressScheduleCall = useCallback(
    async (customEntryPoint?: string) => {
      let careNavigatorLink = userDedicatedCareNavigatorInfo?.ScheduleOnceURL
      if (!isDedicatedCareNavigatorEnabled) {
        try {
          careNavigatorLink = await dispatch(getDirectDedicatedCareNavigatorLink(userId))
        } catch (e) {
          console.error('Failed to get the user’s dedicated care navigator link', e)
          careNavigatorLink = userDirectDedicatedCareNavigatorLink
        }
      }
      const entrypoint = customEntryPoint ?? getEntryPoint()
      dispatch(
        trackEventWithObj({
          event: S4C_EVENTS.CLICK_SCHEDULE_CN_CALL,
          [COMMON_MIXPANEL_PROPERTIES.ACTION]: COMMON_MIXPANEL_EVENTS.CLICK,
          [S4C_PROPERTIES.CN_entrypoint]: entrypoint,
          [S4C_PROPERTIES.ENTRYPOINT_DETAIL]: S4C_EVENTS.CLICK_TO_CONTACT_CN,
        }),
      )
      goToCareNavigatorSchedulingPage({
        defaultDirectCareNavigatorBookingLink,
        directDedicatedCareNavigatorLink: careNavigatorLink,
        firstName,
        lastName,
        email,
        salesforceId,
        launchDate,
        isInternationalUser: isUserInternational,
        lyraId: userId,
        countryIsoCode,
        customerDisplayName,
      })
    },
    [
      countryIsoCode,
      customerDisplayName,
      defaultDirectCareNavigatorBookingLink,
      dispatch,
      email,
      firstName,
      getEntryPoint,
      isDedicatedCareNavigatorEnabled,
      isUserInternational,
      lastName,
      launchDate,
      salesforceId,
      userDedicatedCareNavigatorInfo?.ScheduleOnceURL,
      userDirectDedicatedCareNavigatorLink,
      userId,
    ],
  )

  return useCareNavigatorModalShared({
    isLoggedIn,
    isInternational,
    isLyrian,
    customerPhoneNumber,
    userDedicatedCareNavigatorInfo,
    showLiveChat,
    isDedicatedCareNavigatorEnabled,
    onEntryPointPressedTrack,
    onCareNavigatorIconHoverTrack,
    onPressEmailTrack,
    onPressFAQTrack,
    onPressPhoneCallTrack,
    onPressScheduleCallTrack,
    onPressLiveChatTrack,
    ChatComponent: ChatLive,
    useWebPortalForBottomSheet: true,
    onPressPhoneCallInternational,
    onPressFAQ,
    loadDedicatedCareNavigator,
    onPressScheduleCall,
    countryIsoCode,
    detectedCountryIsoCode,
    activeLanguage,
    customerName,
    countryName,
    showMinimalCTAText,
    showScheduleCall,
    isHomebaseFlow,
    disableBottomSheet,
  })
}
